interface BatteryMarkProps {
  pct: number;
  plugged: boolean;
  fill: string;
  border: string;
  pctColor: string;
}

/** Row-size battery: 33×17 outline + fill bar + nub, bolt overlay while plugged, % text. Column fixed 78px. */
export function BatteryMark({ pct, plugged, fill, border, pctColor }: BatteryMarkProps) {
  return (
    <div className="flex w-[78px] flex-none items-center gap-[7px]">
      <div className="relative h-[17px] w-[33px] flex-none">
        <div className="absolute inset-0 rounded-[5.5px] border-2" style={{ borderColor: border }} />
        <div className="absolute inset-1">
          <div className="h-full rounded-sm" style={{ width: `${pct}%`, background: fill }} />
        </div>
        <div
          className="absolute right-[-4.5px] top-[5.5px] h-[6px] w-[2.5px] rounded-[1px]"
          style={{ background: border }}
        />
        {plugged && (
          <svg width="11" height="15" viewBox="0 0 12 16" className="absolute left-[11px] top-[1px]">
            <path
              d="M7 0 L1 9 L5 9 L4 16 L11 6 L6 6 Z"
              fill="var(--card)"
              stroke="var(--bolt-stroke)"
              strokeWidth="1"
            />
          </svg>
        )}
      </div>
      <span className="text-[13px] font-bold" style={{ color: pctColor }}>
        {pct}%
      </span>
    </div>
  );
}

/** Detail-size battery: 58×28 hero mark. */
export function HeroBattery({ pct, plugged, fill, border }: Omit<BatteryMarkProps, 'pctColor'>) {
  return (
    <div className="relative h-[28px] w-[58px] flex-none">
      <div className="absolute inset-0 rounded-lg border-[2.5px]" style={{ borderColor: border }} />
      <div className="absolute inset-[6px]">
        <div className="h-full rounded-[3px]" style={{ width: `${pct}%`, background: fill }} />
      </div>
      <div
        className="absolute right-[-6px] top-[9px] h-[10px] w-1 rounded-sm"
        style={{ background: border }}
      />
      {plugged && (
        <svg width="18" height="24" viewBox="0 0 12 16" className="absolute left-5 top-0.5">
          <path
            d="M7 0 L1 9 L5 9 L4 16 L11 6 L6 6 Z"
            fill="var(--card)"
            stroke="var(--bolt-stroke)"
            strokeWidth="1"
          />
        </svg>
      )}
    </div>
  );
}

/** Geofence pin: teardrop with punched circle. Row 19×23; detail 23×27. */
export function PinMark({ color, w = 19, h = 23 }: { color: string; w?: number; h?: number }) {
  return (
    <svg width={w} height={h} viewBox="0 0 24 27" className="flex-none">
      <path
        fillRule="evenodd"
        fill={color}
        d="M12 1C7.6 1 4 4.6 4 9c0 6.2 8 17 8 17s8-10.8 8-17c0-4.4-3.6-8-8-8zm0 11.2a3.2 3.2 0 1 1 0-6.4 3.2 3.2 0 0 1 0 6.4z"
      />
    </svg>
  );
}

interface StatusMarkProps {
  err: boolean;
  triColor: string;
  chkColor: string;
  w?: number;
  h?: number;
}

/** Error mark: green checkmark when clear; red triangle with white "!" when in error. Row 22×20; detail 26×24. */
export function StatusMark({ err, triColor, chkColor, w = 22, h = 20 }: StatusMarkProps) {
  return (
    <svg width={w} height={h} viewBox="0 0 18 16" className="flex-none">
      {err ? (
        <>
          <path d="M9 1 L17 15 L1 15 Z" fill={triColor} />
          <text x="9" y="13.4" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#fff">
            !
          </text>
        </>
      ) : (
        <path
          d="M4 8.5 L8 12.5 L15 4.5"
          fill="none"
          stroke={chkColor}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}
